import { useEffect, useState } from 'react';
import {
  cancelAgentAuthorizationIntent,
  describeError,
  listAgentAuthorizationIntents,
  prepareAgentAuthorizationIntent,
  type AgentAuthorizationIntent,
  type AgentAuthorizationIntentList,
  type AgentWriteScope,
  type StepUpProof,
} from '@/lib/api';
import {
  FULL_CONTROL_SCOPE,
  NEVER_DOES,
  anyRequiresPasskey,
  toggleWriteScope,
  writeScopeGroup,
} from '@/lib/agent-authorization';
import { stepUpTarget } from '@/lib/passkeys';
import { useStepUpDialog } from '@/lib/step-up';
import { StepUpDialog } from './StepUpDialog';

/**
 * "Preparar autorização" de um perfil do Hermes em `/agentes` (change platform-mcp-writes,
 * "Consentimento de escrita", passo 0, tarefa 1.18).
 *
 * Sem a preparação, a `/mcp/autorizar` só oferece leitura. Aqui o Admin marca a escrita que o perfil
 * vai poder pedir (as caixas começam desmarcadas), em "Propor (você aprova cada ação)" ou "Fazer
 * sozinha, dentro da ordem de serviço e de limites", ou o controle total, e confirma com o segundo
 * fator: passkey com escrita direta ou controle total, o código do autenticador só com proposta. A
 * preparação vale 10 min, é de uso único e fica presa a esta conta e a esta sessão: o Authenticate do
 * Hermes precisa vir logo depois, no mesmo navegador. A API revalida o perfil, os escopos e o fator.
 */

const STATUS_LABELS: Record<AgentAuthorizationIntent['status'], string> = {
  Prepared: 'preparada',
  Expired: 'vencida',
  Consumed: 'usada no consentimento',
  Cancelled: 'cancelada',
};

function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function PrepareAuthorizationPanel({ clientId, profileName, onClose }: {
  clientId: string;
  profileName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<AgentAuthorizationIntentList | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [selected, setSelected] = useState<AgentWriteScope[]>([]);
  const [full, setFull] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listAgentAuthorizationIntents(clientId)
      .then((list) => {
        if (!cancelled) {
          setData(list);
          setLoadError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setLoadError(describeError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, reloadTick]);

  const scopes: AgentWriteScope[] = full ? [FULL_CONTROL_SCOPE] : selected;
  const passkeyOnly = anyRequiresPasskey(scopes);

  async function prepare(proof: StepUpProof, payload: AgentWriteScope[]) {
    const intent = await prepareAgentAuthorizationIntent({ agentClientId: clientId, scopes: payload }, proof);
    const minutes = payload.includes(FULL_CONTROL_SCOPE) ? data?.fullControlWindowMinutes ?? 5 : data?.intentMinutes ?? 10;
    setNotice(
      `Autorização preparada até ${formatTime(intent.expiresAt)}. Agora, em até ${minutes} min e neste navegador: ` +
        `Hermes → MCP → ${profileName} → vaptapi → Authenticate.`,
    );
    setSelected([]);
    setFull(false);
    setReloadTick((t) => t + 1);
  }

  const stepUp = useStepUpDialog<AgentWriteScope[]>(prepare, (err) => setActionError(describeError(err)), {
    isValid: (payload) => payload.length > 0,
    passkeyTarget: () => stepUpTarget('agentAuthorizationIntentPrepare', clientId),
  });

  async function cancelIntent(id: string) {
    setCancelling(true);
    setActionError(null);
    try {
      await cancelAgentAuthorizationIntent(id);
      setNotice('Preparação cancelada.');
      setReloadTick((t) => t + 1);
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setCancelling(false);
    }
  }

  const available = (data?.availableScopes ?? []).filter((s) => s.scope !== FULL_CONTROL_SCOPE);
  const allowed = available.map((s) => s.scope);
  const current = data?.intents.find((i) => i.status === 'Prepared') ?? null;
  const disabled = stepUp.open || stepUp.busy;

  return (
    <section className="card" aria-label={`Preparar autorização do perfil ${profileName}`} data-testid="prepare-authorization">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div className="card-title">Preparar autorização · <span className="mono">{profileName}</span></div>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={disabled}>
          Fechar
        </button>
      </div>

      {loadError && (
        <div className="error-banner" role="alert">
          {loadError}
        </div>
      )}
      {notice && (
        <div className="info-banner" role="status">
          {notice}
        </div>
      )}
      {actionError && (
        <div className="error-banner" role="alert">
          {actionError}
        </div>
      )}

      {!data && !loadError && <p className="muted">Carregando…</p>}

      {data && !data.writeEligible && (
        <div className="info-banner" role="status" data-testid="prepare-not-eligible">
          Este perfil não pode escrever neste ambiente. A lista de perfis de escrita muda só por PR; o consentimento deste
          perfil oferece só leitura.
        </div>
      )}

      {data && current && (
        <div className="warning-banner" role="note" data-testid="prepare-current" style={{ marginBottom: 8 }}>
          Preparada por {current.preparedByName}
          {current.mine ? ' (nesta sessão)' : ' (em outra sessão; não vale no seu consentimento)'} até{' '}
          {formatTime(current.expiresAt)}: {current.scopes.map((s) => s.scope).join(', ')}.{' '}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={cancelling || disabled}
            onClick={() => void cancelIntent(current.id)}
          >
            Cancelar preparação
          </button>
        </div>
      )}

      {data?.writeEligible && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (scopes.length === 0 || disabled) return;
            setActionError(null);
            setNotice(null);
            stepUp.start(scopes);
          }}
        >
          {(['propose', 'direct'] as const).map((group) => (
            <fieldset key={group} disabled={disabled || full} style={{ border: 0, padding: 0, margin: '0 0 8px' }}>
              <legend style={{ fontWeight: 600, marginBottom: 6 }}>
                {group === 'propose' ? 'Propor (você aprova cada ação)' : 'Fazer sozinha, dentro da ordem de serviço e de limites'}
              </legend>
              {available
                .filter((item) => writeScopeGroup(item.scope) === group)
                .map((item) => (
                  <label key={item.scope} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      checked={selected.includes(item.scope)}
                      onChange={(e) => setSelected(toggleWriteScope(selected, item.scope, e.target.checked, allowed))}
                    />
                    <span>
                      <span className="mono">{item.scope}</span> — {item.label}
                    </span>
                  </label>
                ))}
            </fieldset>
          ))}

          {data.fullControlEligible && (
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, marginBottom: 8 }}>
              <input type="checkbox" checked={full} disabled={disabled} onChange={(e) => setFull(e.target.checked)} />
              Controle total (8 h, até 22:00; o pedido precisa chegar em até {data.fullControlWindowMinutes} min)
            </label>
          )}

          <p className="muted" style={{ margin: '0 0 4px', fontSize: 13 }}>
            Riscos: a proposta só vira efeito com a sua aprovação; a escrita direta age sozinha dentro da ordem de serviço
            e dos limites, e cada efeito gera email. Nunca, nem com controle total: {NEVER_DOES.join('; ')}.
          </p>
          <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>
            {passkeyOnly
              ? 'Escrita direta e controle total pedem confirmação por passkey.'
              : 'Só proposta: confirme com o código do autenticador ou com passkey.'}{' '}
            A preparação vale {data.intentMinutes} min, é de uso único e só vale nesta sessão.
          </p>

          <button type="submit" className="btn btn-sm" disabled={scopes.length === 0 || disabled}>
            Preparar autorização
          </button>
        </form>
      )}

      {data && data.intents.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13 }}>Preparações recentes ({data.intents.length})</summary>
          <ul style={{ fontSize: 13 }}>
            {data.intents.map((intent) => (
              <li key={intent.id}>
                {formatTime(intent.createdAt)} · {STATUS_LABELS[intent.status]} · {intent.preparedByName} ·{' '}
                {intent.stepUpMethod === 'passkey' ? 'passkey' : 'código'} ·{' '}
                {intent.scopes.map((s) => s.scope).join(', ')}
              </li>
            ))}
          </ul>
        </details>
      )}

      {stepUp.open && (
        <StepUpDialog
          title={`Preparar a autorização do perfil ${profileName}`}
          description={
            <p style={{ margin: 0 }}>
              O próximo consentimento deste perfil, aberto nesta sessão em até {data?.intentMinutes ?? 10} min, poderá
              oferecer: <strong>{scopes.includes(FULL_CONTROL_SCOPE) ? 'controle total' : scopes.join(', ')}</strong>.
            </p>
          }
          confirmLabel="Preparar"
          busy={stepUp.busy}
          error={stepUp.error}
          onSubmit={stepUp.submit}
          onCancel={stepUp.cancel}
          onPasskey={stepUp.passkeyAvailable ? stepUp.submitPasskey : undefined}
          passkeyOnly={passkeyOnly}
        />
      )}
    </section>
  );
}
